import { readEnv } from './env.ts';

// Named-service registry. Ported from izteach-platforms-multitenant/helpers/api-environment.ts,
// minus the hardcoded `currentEnv = 'prod'` - the env comes from VITE_API_ENV here.

export type ApiEnv = 'dev' | 'prod';

// IZTeach services this app actually needs today. Add ids from the reference file when more are needed.
export type IzteachService =
  | 'student'
  | 'offlineStack'
  | 'resource'
  | 'utility'
  | 'online'
  | 'exam'
  | 'teacher';

export type ServiceName = 'qlt' | IzteachService;

const GATEWAY_REGION = 'ap-southeast-1';

// The gateway stage is literally "prod" on both envs - dev/prod differ by gateway id, not stage.
const GATEWAY_STAGE = 'prod';

const IZTEACH_GATEWAYS: Record<ApiEnv, Record<IzteachService, string>> = {
  dev: {
    student: 'snajm6o1ik',
    offlineStack: 'wa4rovxtx2',
    resource: '3l6zrsqom1',
    utility: '9262karbyc',
    online: '2s8l9qk84k',
    exam: 'bsoapts5z0',
    teacher: 'xc6ee79peg',
  },
  prod: {
    student: 'fpdamf1645',
    offlineStack: 'f88ih9zvle',
    resource: 'xx7swzlhyl',
    utility: 'c9xi8h3a3d',
    online: 'p9y4zl6vkd',
    exam: 'rt6i5wgkj4',
    teacher: 'gcnj5paf1j',
  },
};

// Fallbacks only. VITE_QLT_API_URL wins so a build can point anywhere.
const QLT_FALLBACK_URL: Record<ApiEnv, string> = {
  dev: 'https://lms-test.smoke-ant.com',
  prod: 'https://ltv-lms-api.mist-ant.com',
};

export function getApiEnv(): ApiEnv {
  return readEnv('VITE_API_ENV') === 'prod' ? 'prod' : 'dev';
}

export function isIzteachService(name: ServiceName): name is IzteachService {
  return name !== 'qlt';
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

export function getQltUrl(): string {
  const env = getApiEnv();
  return stripTrailingSlash(readEnv('VITE_QLT_API_URL') || QLT_FALLBACK_URL[env]);
}

export function getGatewayId(service: IzteachService): string {
  const id = IZTEACH_GATEWAYS[getApiEnv()][service];
  if (!id) throw new Error(`[api] no gateway id for service "${service}"`);
  return id;
}

// Full base URL for any named service. QLT is a plain host, IZTeach services are composed gateway URLs.
export function getServiceUrl(service: ServiceName): string {
  if (!isIzteachService(service)) return getQltUrl();
  const id = getGatewayId(service);
  return `https://${id}.execute-api.${GATEWAY_REGION}.amazonaws.com/${GATEWAY_STAGE}`;
}

// Default vendor id for the QLT tenant. Overridable per build; the session store may also set it at runtime.
export function getDefaultVendorId(): string {
  return readEnv('VITE_IZT_VENDOR_ID') || '';
}

// Tenant whose library (courses, documents) the kho screens browse - lms-api's izt-content hardcodes the same one. Empty means the session tenant.
export function getContentVendorId(): string {
  return readEnv('VITE_IZT_CONTENT_VENDOR_ID') || '';
}

// Shared VOD host grant-video serves unsigned vendors from (the backend's CDN_VOD_HOST), bare hostname.
// Empty turns the cross-vendor playback fallback off.
export function getVodHost(): string {
  return readEnv('VITE_IZT_VOD_HOST') || '';
}

// Domain lms-api gives a student's IZ email, `${code}@<domain>`; Bài nộp finds submissions by it. Empty means unconfigured.
export function getStudentEmailDomain(): string {
  return readEnv('VITE_IZT_STUDENT_EMAIL_DOMAIN') || '';
}
